import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { askAI } from './ai';
import { Card } from './Shared';

function score(s) {
  const demand = Math.min(10, s.dispatch + s.declined * 1.5);
  const supply = Math.min(10, s.placement * 2 + s.enrollment * 0.5);
  const ahj = Math.min(10, s.ahj * 3 + s.mandate * 4);
  const cross = Math.min(10, (s.dispatch > 0 ? 3 : 0) + (s.enrollment > 0 ? 3 : 0) + (s.ahj > 0 ? 4 : 0));
  const total = (demand + supply + ahj + cross) / 4;
  return { demand: Math.round(demand*10)/10, supply: Math.round(supply*10)/10, ahj: Math.round(ahj*10)/10, cross: Math.round(cross*10)/10, total: Math.round(total*10)/10 };
}

const tierLabel = t => t >= 7 ? 'Expand' : t >= 4 ? 'Monitor' : 'Watch';
const tierVariant = t => t >= 7 ? 'badge-green' : t >= 4 ? 'badge-amber' : 'badge-gray';

export default function Matrix() {
  const [signals, setSignals] = useState({});
  const [aiOut, setAiOut] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [analyzingState, setAnalyzingState] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    const [{ data: d }, { data: e }, { data: a }] = await Promise.all([
      supabase.from('dispatches').select('state,status'),
      supabase.from('enrollments').select('state,placement_interest'),
      supabase.from('ahj_pipeline').select('state,pipeline_stage')
    ]);
    const s = {};
    const ensure = st => { if (st && !s[st]) s[st] = { dispatch:0, declined:0, enrollment:0, placement:0, ahj:0, mandate:0 }; };
    (d||[]).forEach(r => { if (r.state) { ensure(r.state); if (r.status && r.status.startsWith('Declined')) s[r.state].declined++; else s[r.state].dispatch++; }});
    (e||[]).forEach(r => { if (r.state) { ensure(r.state); s[r.state].enrollment++; if (r.placement_interest==='yes') s[r.state].placement++; }});
    (a||[]).forEach(r => { if (r.state) { ensure(r.state); s[r.state].ahj++; if (r.pipeline_stage==='mandate') s[r.state].mandate++; }});
    setSignals(s);
  }

  const scored = Object.entries(signals).map(([state, s]) => ({ state, raw: s, ...score(s) })).sort((a,b) => b.total - a.total);

  async function runAI(stateData) {
    setShowAi(true); setAiLoading(true); setAiOut('');
    let prompt;
    if (stateData) {
      setAnalyzingState(stateData.state);
      prompt = `You are analyzing a single market for fire watch expansion for AO Group Holdings (Alpha Omega brand). State: ${stateData.state}. Signal counts — dispatches: ${stateData.raw.dispatch}, declined: ${stateData.raw.declined}, enrollments: ${stateData.raw.enrollment}, placement-interested: ${stateData.raw.placement}, AHJ contacts: ${stateData.raw.ahj}, mandates: ${stateData.raw.mandate}. Scores — demand: ${stateData.demand}/10, supply: ${stateData.supply}/10, AHJ: ${stateData.ahj}/10, cross-entity: ${stateData.cross}/10, overall: ${stateData.total}/10. In 3 sentences: what this score profile means, the primary gap blocking readiness, and the single action that would most improve this state's position. Be direct.`;
    } else {
      setAnalyzingState(null);
      const summary = scored.map(s => `${s.state}: demand ${s.demand}, supply ${s.supply}, AHJ ${s.ahj}, cross ${s.cross}, total ${s.total}`).join('; ') || 'no data';
      prompt = `You are scoring markets for fire watch expansion for AO Group Holdings (Alpha Omega brand). Market scores (0-10 per dimension): ${summary}. Rank the top 3 expansion markets and in 3 sentences explain which state has the strongest converging signals, which has the most critical gap to close, and the recommended expansion sequence. Be direct and data-driven.`;
    }
    try {
      setAiOut(await askAI(prompt, 1000));
    } catch (err) { setAiOut('AI error: ' + err.message); }
    setAiLoading(false);
  }

  return (
    <div>
      <div style={{ fontSize:'13px', color:'#64748B', marginBottom:'16px', lineHeight:'1.6' }}>
        Scoring 4 dimensions: demand strength · contractor supply · AHJ readiness · cross-entity breadth. Auto-populated from dispatch, enrollment, and AHJ records. AI scoring uses aggregated signal counts only — no PII transmitted.
      </div>

      <Card>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
          <span className="card-title">Market scores</span>
          <button onClick={() => runAI(null)} disabled={aiLoading} className="btn btn-primary btn-sm">
            {aiLoading && !analyzingState ? 'Scoring…' : 'Score all markets →'}
          </button>
        </div>

        {!scored.length ? (
          <div className="empty-state">Add dispatch records, enrollments, and AHJ entries to populate the matrix.</div>
        ) : (
          <>
            <div className="matrix-header">
              {['State','Overall','Demand','Supply','AHJ','Cross','Tier',''].map((h,i) => <div key={i} className="matrix-col-label">{h}</div>)}
            </div>
            {scored.map(s => (
              <div key={s.state} className="matrix-row">
                <div style={{ fontSize:'13px', fontWeight:'600' }}>{s.state}</div>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <div className="score-bar-wrap"><div className="score-bar-fill" style={{ width: Math.min(100,(s.total/10)*100)+'%' }} /></div>
                  <span style={{ fontSize:'12px', fontWeight:'600', color:'#475569', minWidth:'24px' }}>{s.total}</span>
                </div>
                {[s.demand, s.supply, s.ahj, s.cross].map((v,i) => <div key={i} className="matrix-score-num">{v}</div>)}
                <span className={"badge " + tierVariant(s.total)}>{tierLabel(s.total)}</span>
                <button onClick={() => runAI(s)} className="btn btn-secondary btn-sm" style={{ fontSize:'11px' }}>Analyze →</button>
              </div>
            ))}
          </>
        )}
      </Card>

      {showAi && (
        <div className="ai-panel">
          <div className="ai-panel-label"><div className="ai-panel-dot" />AI SCORING — {analyzingState || 'ALL MARKETS'}</div>
          <div className="ai-panel-output">
            {aiLoading ? <span style={{ color:'#94A3B8' }}>Analyzing market signals…</span> : aiOut}
          </div>
        </div>
      )}
    </div>
  );
}
